export type SetupItem = { id: string } & Record<string, unknown>;

export interface FieldOption {
  label: string;
  value: string;
}

export interface SetupField {
  defaultValue?: boolean | number | string;
  help?: string;
  key: string;
  label: string;
  options?: readonly FieldOption[];
  placeholder?: string;
  required?: boolean;
  type?:
    "checkbox" | "date" | "email" | "number" | "select" | "text" | "textarea";
}

export interface SetupResource {
  addLabel: string;
  archiveLabel?: string;
  description: string;
  fields: readonly SetupField[];
  key: string;
  noun: string;
  path: string;
  title: string;
}

const option = (value: string, label: string): FieldOption => ({
  label,
  value,
});

export const areaResource: SetupResource = {
  addLabel: "Add area",
  description: "Map the rooms and work zones staff use when logging checks.",
  fields: [
    {
      key: "name",
      label: "Area name",
      placeholder: "Main prep bench",
      required: true,
    },
    {
      key: "kind",
      label: "Area type",
      options: [
        option("prep", "Preparation"),
        option("storage", "Storage"),
        option("cold_storage", "Cold storage"),
        option("wash", "Washing"),
        option("waste", "Waste"),
        option("service", "Service"),
        option("office", "Office"),
        option("exterior", "Exterior"),
        option("other", "Other"),
      ],
      required: true,
      type: "select",
    },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  key: "areas",
  noun: "area",
  path: "areas",
  title: "Kitchen areas",
};

export const assetResource: SetupResource = {
  addLabel: "Add equipment",
  archiveLabel: "Retire",
  description:
    "Fridges, probes and other equipment that appear in kitchen records.",
  fields: [
    {
      key: "name",
      label: "Equipment name",
      placeholder: "Walk-in fridge",
      required: true,
    },
    {
      key: "asset_type",
      label: "Equipment type",
      required: true,
      type: "select",
      options: [
        option("fridge", "Fridge"),
        option("freezer", "Freezer"),
        option("chiller", "Chiller"),
        option("hot_hold", "Hot hold"),
        option("cook_equipment", "Cooking equipment"),
        option("dishwasher", "Dishwasher"),
        option("thermometer", "Thermometer"),
        option("probe", "Probe"),
        option("water_system", "Water system"),
        option("water_tank", "Water tank"),
        option("smoker", "Smoker"),
        option("water_bath", "Water bath"),
        option("dehydrator", "Dehydrator"),
        option("vehicle", "Vehicle"),
        option("other", "Other"),
      ],
    },
    { key: "area_id", label: "Area", type: "select" },
    { key: "code", label: "Kitchen code", placeholder: "FR-01" },
    {
      defaultValue: "normal",
      key: "criticality",
      label: "Criticality",
      type: "select",
      options: [option("normal", "Normal"), option("critical", "Critical")],
    },
    { key: "target_min_c", label: "Minimum °C", type: "number" },
    { key: "target_max_c", label: "Maximum °C", type: "number" },
    {
      key: "calibration_interval_days",
      label: "Calibration interval (days)",
      type: "number",
    },
    { key: "last_calibrated_on", label: "Last calibrated", type: "date" },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  key: "assets",
  noun: "equipment item",
  path: "assets",
  title: "Equipment and QR labels",
};

export const supplierResource: SetupResource = {
  addLabel: "Add supplier",
  description: "Approved suppliers used for delivery checks and evidence.",
  fields: [
    {
      key: "name",
      label: "Supplier name",
      placeholder: "Fresh Foods NZ",
      required: true,
    },
    { key: "site_registration_no", label: "Registration number" },
    {
      key: "registration_expires_on",
      label: "Registration expires",
      type: "date",
    },
    { key: "contact_person", label: "Contact person" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email", type: "email" },
    {
      key: "goods_summary",
      label: "Goods supplied",
      placeholder: "Chilled dairy and produce",
      type: "textarea",
    },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  key: "suppliers",
  noun: "supplier",
  path: "suppliers",
  title: "Suppliers",
};

export const cleaningResource: SetupResource = {
  addLabel: "Add cleaning item",
  description: "Define what is cleaned, how often, and the approved method.",
  fields: [
    {
      key: "target",
      label: "What is cleaned",
      placeholder: "Main prep benches",
      required: true,
    },
    {
      key: "frequency",
      label: "Frequency",
      placeholder: "After each use",
      required: true,
    },
    {
      key: "method",
      label: "Cleaning method",
      required: true,
      type: "textarea",
    },
    { key: "chemical", label: "Chemical or product" },
    { key: "responsibility", label: "Responsible role" },
    { key: "safety_notes", label: "Safety notes", type: "textarea" },
    {
      defaultValue: false,
      key: "task_logging_enabled",
      label: "Create recurring cleaning tasks",
      type: "checkbox",
    },
  ],
  key: "cleaning",
  noun: "cleaning item",
  path: "cleaning-plan-items",
  title: "Cleaning plan",
};

export const waterResource: SetupResource = {
  addLabel: "Add water source",
  description: "Record every water supply and the controls that make it safe.",
  fields: [
    {
      key: "kind",
      label: "Source type",
      required: true,
      type: "select",
      options: [
        option("registered_supply", "Registered supply"),
        option("roof", "Roof water"),
        option("bore", "Bore"),
        option("surface", "Surface water"),
        option("tanker", "Tanker"),
        option("other", "Other"),
      ],
    },
    { key: "supplier_name", label: "Supplier name" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "treatment", label: "Treatment", type: "textarea" },
    {
      defaultValue: "ok",
      key: "safe_status",
      label: "Safety status",
      type: "select",
      options: [
        option("ok", "Safe"),
        option("attention", "Needs attention"),
        option("unsafe", "Unsafe"),
      ],
    },
  ],
  key: "water",
  noun: "water source",
  path: "water-sources",
  title: "Water sources",
};

export const riskResource: SetupResource = {
  addLabel: "Add nearby risk",
  description:
    "Capture nearby hazards and the practical controls your kitchen uses.",
  fields: [
    {
      key: "hazard",
      label: "Hazard",
      placeholder: "Dust from neighbouring construction",
      required: true,
      type: "textarea",
    },
    { key: "source", label: "Source" },
    { key: "control", label: "Control", required: true, type: "textarea" },
    {
      defaultValue: "active",
      key: "status",
      label: "Status",
      type: "select",
      options: [option("active", "Active"), option("resolved", "Resolved")],
    },
  ],
  key: "risks",
  noun: "nearby risk",
  path: "nearby-risks",
  title: "Nearby risks",
};

export const separationResource: SetupResource = {
  addLabel: "Add separation control",
  description:
    "Document how raw food, allergens, chemicals and foreign matter are separated.",
  fields: [
    {
      key: "control_type",
      label: "Control type",
      required: true,
      type: "select",
      options: [
        option("raw_cooked", "Raw and cooked"),
        option("allergen", "Allergen"),
        option("chemical", "Chemical"),
        option("foreign_matter", "Foreign matter"),
        option("other", "Other"),
      ],
    },
    { key: "hazard", label: "Hazard", required: true, type: "textarea" },
    {
      key: "method",
      label: "Separation method",
      required: true,
      type: "textarea",
    },
    { key: "areas_note", label: "Areas or storage note", type: "textarea" },
    { key: "review_due", label: "Review due", type: "date" },
  ],
  key: "separation",
  noun: "separation control",
  path: "separation-controls",
  title: "Separation controls",
};

export const methodResource: SetupResource = {
  addLabel: "Add process method",
  description:
    "Write down specialised methods and track whether evidence has proved them.",
  fields: [
    { key: "name", label: "Method name", required: true },
    {
      key: "record_type_code",
      label: "Process",
      required: true,
      type: "select",
      options: [
        option("cook", "Cooking"),
        option("reheat", "Reheating"),
        option("cooling", "Cooling"),
        option("water_activity", "Water activity"),
        option("acidification", "Acidification"),
        option("hot_smoke", "Hot smoking"),
        option("sous_vide", "Sous vide"),
        option("duck_drying", "Duck drying"),
      ],
    },
    { key: "food_description", label: "Food", type: "textarea" },
    { key: "method_text", label: "Method", type: "textarea" },
    { key: "equipment_text", label: "Equipment", type: "textarea" },
    {
      defaultValue: true,
      key: "weekly_check_due",
      label: "Weekly proof check is required",
      type: "checkbox",
    },
  ],
  key: "methods",
  noun: "process method",
  path: "process-methods",
  title: "Process methods",
};

export const documentResource: SetupResource = {
  addLabel: "Add document",
  description:
    "Keep licences, certificates, layouts and verifier evidence easy to find.",
  fields: [
    { key: "title", label: "Document title", required: true },
    {
      key: "doc_type",
      label: "Document type",
      required: true,
      type: "select",
      options: [
        option("site_layout", "Site layout"),
        option("site_map", "Site map"),
        option("manager_certificate", "Manager certificate"),
        option("lcq", "LCQ"),
        option("alcohol_licence", "Alcohol licence"),
        option("pest_contract", "Pest contract"),
        option("pest_report", "Pest report"),
        option("water_test_certificate", "Water test certificate"),
        option("equipment_service", "Equipment service"),
        option("registration_certificate", "Registration certificate"),
        option("verification_report", "Verification report"),
        option("supplier_evidence", "Supplier evidence"),
        option("other", "Other"),
      ],
    },
    { key: "reference_no", label: "Reference number" },
    { key: "issuer", label: "Issuer" },
    { key: "issued_on", label: "Issued on", type: "date" },
    { key: "expires_on", label: "Expires on", type: "date" },
    { key: "notes", label: "Notes", type: "textarea" },
    {
      defaultValue: false,
      key: "confidential",
      label: "Confidential document",
      type: "checkbox",
    },
    {
      defaultValue: true,
      key: "create_reminder",
      label: "Create an expiry reminder",
      type: "checkbox",
    },
  ],
  key: "documents",
  noun: "document",
  path: "documents",
  title: "Documents",
};

export const reminderResource: SetupResource = {
  addLabel: "Add reminder",
  archiveLabel: "Dismiss",
  description:
    "Stay ahead of registration, verification, service and licence dates.",
  fields: [
    { key: "title", label: "Reminder title", required: true },
    {
      key: "subject_kind",
      label: "Reminder type",
      required: true,
      type: "select",
      options: [
        option("fcp_registration", "FCP registration"),
        option("verification", "Verification"),
        option("manager_certificate", "Manager certificate"),
        option("lcq", "LCQ"),
        option("alcohol_licence", "Alcohol licence"),
        option("pest_service", "Pest service"),
        option("equipment_service", "Equipment service"),
        option("thermometer_calibration", "Thermometer calibration"),
        option("document_expiry", "Document expiry"),
        option("custom", "Custom"),
      ],
    },
    { key: "due_on", label: "Due on", required: true, type: "date" },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  key: "reminders",
  noun: "reminder",
  path: "renewal-reminders",
  title: "Renewal reminders",
};

export const setupResources = [
  areaResource,
  assetResource,
  supplierResource,
  cleaningResource,
  waterResource,
  riskResource,
  separationResource,
  methodResource,
  documentResource,
  reminderResource,
] as const;

export function labelFor(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function itemTitle(resource: SetupResource, item: SetupItem): string {
  for (const key of [
    "name",
    "title",
    "target",
    "hazard",
    "supplier_name",
    "kind",
  ]) {
    const value = item[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return labelFor(resource.noun);
}

export function itemSummary(resource: SetupResource, item: SetupItem): string {
  const keys = resource.fields
    .map((field) => field.key)
    .filter(
      (key) => !["name", "title", "target", "hazard", "notes"].includes(key),
    );
  const parts = keys.flatMap((key) => {
    const value = item[key];
    if (typeof value === "string" && value.length > 0) return [labelFor(value)];
    if (typeof value === "number") return [String(value)];
    return [];
  });
  return parts.slice(0, 3).join(" · ") || "Ready for kitchen use";
}

export function setupCompletion(counts: Record<string, number>): number {
  const ready = [
    "areas",
    "assets",
    "suppliers",
    "cleaning",
    "water",
    "methods",
  ].filter((key) => (counts[key] ?? 0) > 0).length;
  return Math.round((ready / 6) * 100);
}
